import { Box, Typography, useTheme } from "@mui/material";
import type { StructuredExtractedFields } from "@/modules/zoom-calls/types";
import {
  formatLabel,
  isHiddenTechnicalKey,
} from "@/modules/zoom-calls/components/analysis.helpers";
import {
  formatAnyValue,
  NOTES_EXCLUDED_KEYS,
} from "./extracted-fields.helpers";

/**
 * "Notes" card for the Analysis tab: the top-level call-summary flags of the
 * structured extracted-fields payload (budget_discussed, objections_raised,
 * buying_intent, client_residency, …), rendered generically so every key/value the
 * backend sends is shown and nothing is hardcoded or derived.
 */
const NotesCard = ({ data }: { data: StructuredExtractedFields | null }) => {
  const theme = useTheme();
  const p = theme.nawy.colors.primitives;

  const rows: Array<{ label: string; value: string }> = data
    ? Object.entries(data)
        .filter(
          ([key]) => !NOTES_EXCLUDED_KEYS.has(key) && !isHiddenTechnicalKey(key),
        )
        .map(([key, raw]) => {
          const value = formatAnyValue(raw);
          return value ? { label: formatLabel(key), value } : null;
        })
        .filter((r): r is { label: string; value: string } => !!r)
    : [];

  const notes = Array.isArray(data?.client_notes)
    ? data.client_notes.map((n) => String(n).trim()).filter(Boolean)
    : [];

  // Nothing to show → render nothing (no empty "Notes" card).
  if (rows.length === 0 && notes.length === 0) return null;

  return (
    <Box
      sx={{
        p: "12px",
        borderRadius: "12px",
        backgroundColor: theme.nawy.colors.semantic.layer.secondary,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 400,
          lineHeight: "16px",
          color: theme.nawy.colors.semantic.text.tertiary,
          whiteSpace: "nowrap",
        }}
      >
        Notes
      </Typography>

      {rows.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            columnGap: "16px",
            rowGap: "6px",
          }}
        >
          {rows.map((row) => (
            <Box key={row.label} sx={{ display: "contents" }}>
              <Typography
                sx={{ fontSize: 12, color: p.grey[700], whiteSpace: "nowrap" }}
              >
                {row.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: p.navy[800],
                  wordBreak: "break-word",
                }}
              >
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {notes.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {notes.map((note, i) => (
            <Typography
              key={i}
              sx={{ fontSize: 12, color: p.grey[700], wordBreak: "break-word" }}
            >
              {`• ${note}`}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default NotesCard;
